import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import {
  Calendar, TrendingUp, Coins, Receipt, ArrowUpRight, ArrowDownRight,
  Award, CreditCard, Loader2, AlertCircle, PieChart, BarChart3, Trophy, Sparkles
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from 'recharts';

interface LifetimeData {
  store: string;
  currency: string;
  lifetime: {
    totalRevenue: number;
    netProfit: number;
    transactions: number;
    totalRefunds: number;
    totalCogs: number;
    totalCommission: number;
    totalDebt: number;
    totalUnpaidBroker: number;
    averageOrderValue: number;
    topCategory: string;
  };
  topCategories: { name: string; revenue: number }[];
  paymentMix: { method: string; key: string; count: number; amount: number }[];
  monthlyTrend: { month: string; label: string; revenue: number; transactions: number }[];
  yearlyTrend: { year: string; revenue: number; transactions: number }[];
  bestDay: string | null;
  worstDay: string | null;
}

const PAYMENT_COLORS: Record<string, string> = {
  cash: '#10b981', card: '#8b5cf6', mobile: '#0ea5e9', split: '#f59e0b',
  bank_transfer: '#14b8a6', cheque: '#ec4899', gift_card: '#f43f5e',
  store_credit: '#6366f1', qr_code: '#22c55e', other: '#94a3b8',
};

const LifetimeAnalytics: React.FC<{ storeApiKey: string }> = ({ storeApiKey }) => {
  const [data, setData] = useState<LifetimeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const formatRs = (amount: number) =>
    'Rs. ' + amount.toLocaleString('en-LK', { maximumFractionDigits: 0 });

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('kangavena:cashierToken') || '';
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch('/api/analytics/lifetime', {
          headers: {
            'x-store-api-key': storeApiKey,
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          cache: 'no-store',
        });
        if (!res.ok) throw new Error('Failed to load lifetime analytics');
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e: any) {
        if (!cancelled) setError(e.message || 'Lifetime analytics unavailable');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [storeApiKey]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-400 text-[12px]">
        <Loader2 size={20} className="animate-spin mr-2" /> Loading lifetime records…
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 px-4">
        <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-[12px]">
          <AlertCircle size={16} /> {error}
        </div>
      </div>
    );
  }

  if (!data) return null;
  const L = data.lifetime;

  const KpiCard = ({ label, value, icon: Icon, color, sub }: {
    label: string; value: string; icon: any; color: string; sub?: string;
  }) => (
    <div className="rounded-2xl border border-slate-200 bg-white/80 backdrop-blur-sm p-4 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{label}</span>
        <Icon size={15} className={color} />
      </div>
      <p className="text-[18px] sm:text-[22px] font-black text-slate-800 font-mono leading-none">{value}</p>
      {sub && <p className="text-[9px] text-slate-400 mt-1 font-medium">{sub}</p>}
    </div>
  );

  const maxMonth = Math.max(...data.monthlyTrend.map(m => m.revenue), 1);

  return (
    <div className="p-3 sm:p-6 space-y-5">
      {/* Hero banner */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-violet-50 p-5 shadow-sm"
      >
        <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-indigo-400/10 blur-[60px] pointer-events-none" />
        <div className="relative flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-md shrink-0">
            <Trophy size={20} className="text-white" />
          </div>
          <div>
            <h2 className="text-[15px] font-black text-slate-800 tracking-tight">Lifetime Analytics</h2>
            <p className="text-[10px] text-slate-500">All-time records for {data.store} • since first sale</p>
          </div>
        </div>
      </motion.div>

      {/* Lifetime KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard label="Lifetime Revenue" value={formatRs(L.totalRevenue)} icon={Coins} color="text-emerald-500" sub="All completed sales" />
        <KpiCard label="Lifetime Net Profit" value={formatRs(L.netProfit)} icon={TrendingUp} color="text-teal-500" sub="After COGS, commission, refunds" />
        <KpiCard label="Total Transactions" value={L.transactions.toLocaleString()} icon={Receipt} color="text-sky-500" sub={`Avg ${formatRs(L.averageOrderValue)} / bill`} />
        <KpiCard label="Top Category" value={L.topCategory} icon={Award} color="text-amber-500" sub="Best selling (lifetime)" />
      </div>

      {/* Secondary lifetime metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard label="Total COGS" value={formatRs(L.totalCogs)} icon={BarChart3} color="text-orange-500" />
        <KpiCard label="Total Commission" value={formatRs(L.totalCommission)} icon={Sparkles} color="text-violet-500" />
        <KpiCard label="Total Refunds" value={formatRs(L.totalRefunds)} icon={ArrowDownRight} color="text-rose-500" />
        <KpiCard label="Outstanding Debt" value={formatRs(L.totalDebt)} icon={CreditCard} color="text-red-500" sub={`Broker due: ${formatRs(L.totalUnpaidBroker)}`} />
      </div>

      {/* Monthly trend */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="rounded-2xl border border-slate-200 bg-white/90 backdrop-blur-sm p-4 sm:p-5 shadow-sm"
      >
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
            <TrendingUp size={14} className="text-white" />
          </div>
          <div>
            <h3 className="text-[13px] font-bold text-slate-800">Monthly Revenue (All-Time)</h3>
            <p className="text-[9px] text-slate-500">Every month since you started</p>
          </div>
        </div>
        <div className="h-56">
          {data.monthlyTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.monthlyTrend} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="lifetimeMonthGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={(v: number) => `Rs.${(v / 1000).toFixed(0)}k`} />
                <Tooltip
                  contentStyle={{ backgroundColor: 'rgba(255,255,255,0.95)', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '11px' }}
                  formatter={(value: any) => [formatRs(value), 'Revenue']}
                />
                <Area type="monotone" dataKey="revenue" stroke="#6366f1" strokeWidth={2.5} fill="url(#lifetimeMonthGrad)" dot={false} activeDot={{ r: 4, stroke: '#6366f1', strokeWidth: 2, fill: 'white' }} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-slate-400 text-[11px]">No sales recorded yet</div>
          )}
        </div>
      </motion.div>

      {/* Yearly trend */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="rounded-2xl border border-slate-200 bg-white/90 backdrop-blur-sm p-4 sm:p-5 shadow-sm"
      >
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center">
            <Calendar size={14} className="text-white" />
          </div>
          <div>
            <h3 className="text-[13px] font-bold text-slate-800">Yearly Revenue (All-Time)</h3>
            <p className="text-[9px] text-slate-500">Annual performance</p>
          </div>
        </div>
        <div className="h-44">
          {data.yearlyTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.yearlyTrend} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                <XAxis dataKey="year" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={(v: number) => `Rs.${(v / 1000).toFixed(0)}k`} />
                <Tooltip contentStyle={{ backgroundColor: 'rgba(255,255,255,0.95)', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '11px' }} formatter={(value: any) => [formatRs(value), 'Revenue']} />
                <Bar dataKey="revenue" radius={[6, 6, 0, 0]} fill="#0ea5e9" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-slate-400 text-[11px]">No sales recorded yet</div>
          )}
        </div>
      </motion.div>

      {/* Two-column: Top categories + Payment mix */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top categories */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="rounded-2xl border border-slate-200 bg-white/90 backdrop-blur-sm p-4 sm:p-5 shadow-sm"
        >
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
              <Award size={14} className="text-white" />
            </div>
            <h3 className="text-[13px] font-bold text-slate-800">Top Categories (Lifetime)</h3>
          </div>
          {data.topCategories.length > 0 ? (
            <div className="space-y-2">
              {data.topCategories.slice(0, 8).map((c, i) => {
                const pct = L.totalRevenue > 0 ? (c.revenue / L.totalRevenue) * 100 : 0;
                return (
                  <div key={c.name} className="flex items-center gap-3">
                    <span className="text-[10px] font-black text-slate-400 w-5 shrink-0">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] font-bold text-slate-700 truncate">{c.name}</span>
                        <span className="text-[10px] font-mono text-slate-500 shrink-0 ml-2">{formatRs(c.revenue)}</span>
                      </div>
                      <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full bg-gradient-to-r from-amber-400 to-orange-500" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400 text-[11px]">No category data</div>
          )}
        </motion.div>

        {/* Payment mix */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="rounded-2xl border border-slate-200 bg-white/90 backdrop-blur-sm p-4 sm:p-5 shadow-sm"
        >
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <PieChart size={14} className="text-white" />
            </div>
            <h3 className="text-[13px] font-bold text-slate-800">Payment Method Mix</h3>
          </div>
          {data.paymentMix.length > 0 ? (
            <div className="space-y-2">
              {data.paymentMix.map((p) => {
                const pct = L.totalRevenue > 0 ? (p.amount / L.totalRevenue) * 100 : 0;
                const color = PAYMENT_COLORS[p.key] || '#94a3b8';
                return (
                  <div key={p.key} className="flex items-center gap-3">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] font-bold text-slate-700 truncate">{p.method}</span>
                        <span className="text-[10px] font-mono text-slate-500 shrink-0 ml-2">{p.count} bills · {pct.toFixed(0)}%</span>
                      </div>
                      <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400 text-[11px]">No payment data</div>
          )}
        </motion.div>
      </div>

      {/* Best / worst day */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="grid grid-cols-1 sm:grid-cols-2 gap-3"
      >
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-3 sm:p-4 flex items-center gap-3">
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 shrink-0">
            <ArrowUpRight size={16} />
          </div>
          <div className="min-w-0">
            <p className="text-[8px] sm:text-[9px] font-black text-emerald-700 uppercase tracking-wider">Best Day (Lifetime)</p>
            <p className="text-[13px] sm:text-[16px] font-black text-emerald-800 truncate">{data.bestDay || '—'}</p>
          </div>
        </div>
        <div className="rounded-2xl border border-rose-200 bg-rose-50/60 p-3 sm:p-4 flex items-center gap-3">
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-rose-100 flex items-center justify-center text-rose-600 shrink-0">
            <ArrowDownRight size={16} />
          </div>
          <div className="min-w-0">
            <p className="text-[8px] sm:text-[9px] font-black text-rose-700 uppercase tracking-wider">Slowest Day (Lifetime)</p>
            <p className="text-[13px] sm:text-[16px] font-black text-rose-800 truncate">{data.worstDay || '—'}</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default LifetimeAnalytics;
